import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllSellers, getPropertiesBySeller } from '../../Redux/Actions/actions';
import CardSellers from '../CardSellers/CardSellers';
import { jsPDF } from 'jspdf';
import styles from './CardSellersContainer.module.css';

const CardSellersContainer = () => {
  const dispatch = useDispatch();
  const sellers = useSelector((state) => state.sellers);
  const sellerProperties = useSelector((state) => state.sellerProperties);
  const error = useSelector((state) => state.error);

  const [filter, setFilter] = useState('active');

  useEffect(() => {
    dispatch(getAllSellers());
  }, [dispatch]);

  useEffect(() => {
    if (sellers && sellers.length > 0) {
      sellers.forEach((seller) => {
        dispatch(getPropertiesBySeller(seller.id));
      });
    }
  }, [sellers, dispatch]);

  const sortByName = (list) =>
    [...list].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
    );

  const filteredSellers = useMemo(() => {
    if (!Array.isArray(sellers)) return [];
    const base = sellers.filter((s) => {
      if (filter === 'all') return true;
      if (filter === 'active') return s.status === true;
      if (filter === 'inactive') return s.status === false;
      return true;
    });
    return sortByName(base);
  }, [sellers, filter]);

  const numVendedores = filteredSellers.length;

  const totalPropertiesCount = filteredSellers.reduce((total, seller) => {
    const propertiesCount = sellerProperties?.[seller.id]?.length || 0;
    return total + propertiesCount;
  }, 0);

  const generatePDF = () => {
    if (!Array.isArray(sellers) || sellers.length === 0) {
      alert('No hay vendedores para exportar.');
      return;
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 12;
    let y = 18;

    // Header
    doc.setFillColor(61, 47, 135);
    doc.rect(0, 0, pageWidth, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('BY Raíces · Listado de Vendedores', marginX, 8);

    y = 22;
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const fecha = new Date().toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
    doc.text(`Fecha: ${fecha}`, marginX, y);
    doc.text(`Total de vendedores: ${sellers.length}`, pageWidth - marginX, y, { align: 'right' });
    y += 8;

    const activos = sortByName(sellers.filter((s) => s.status === true));
    const inactivos = sortByName(sellers.filter((s) => s.status === false));

    // Column layout
    const colNombre = marginX;
    const colApellido = marginX + 45;
    const colMail = marginX + 90;
    const colTel = pageWidth - marginX - 35;

    const drawTableHeader = () => {
      doc.setFillColor(240, 238, 249);
      doc.rect(marginX - 2, y - 4, pageWidth - 2 * marginX + 4, 7, 'F');
      doc.setTextColor(61, 47, 135);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Nombre', colNombre, y);
      doc.text('Apellido', colApellido, y);
      doc.text('Email', colMail, y);
      doc.text('Teléfono', colTel, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
    };

    const ensureSpace = (needed = 14) => {
      if (y + needed > pageHeight - 12) {
        doc.addPage();
        y = 18;
        return true;
      }
      return false;
    };

    const truncate = (str, max) => {
      const s = String(str || '-');
      return s.length > max ? s.slice(0, max - 1) + '…' : s;
    };

    const drawSection = (titulo, lista, color) => {
      ensureSpace(20);
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(marginX - 2, y - 4, pageWidth - 2 * marginX + 4, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`${titulo} (${lista.length})`, marginX, y + 1);
      y += 9;

      if (lista.length === 0) {
        doc.setTextColor(140, 140, 150);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.text('Sin vendedores en esta categoría.', marginX, y);
        y += 8;
        return;
      }

      drawTableHeader();

      doc.setFontSize(9);
      lista.forEach((seller, idx) => {
        if (ensureSpace(7)) drawTableHeader();

        if (idx % 2 === 0) {
          doc.setFillColor(250, 250, 253);
          doc.rect(marginX - 2, y - 4, pageWidth - 2 * marginX + 4, 6, 'F');
        }

        doc.setTextColor(40, 40, 40);
        doc.text(truncate(seller.name, 22), colNombre, y);
        doc.text(truncate(seller.last_name, 22), colApellido, y);
        doc.text(truncate(seller.mail, 38), colMail, y);
        doc.text(truncate(seller.phone_number, 18), colTel, y);
        y += 6;
      });
      y += 4;
    };

    drawSection('VENDEDORES ACTIVOS', activos, [61, 47, 135]);
    drawSection('VENDEDORES INACTIVOS', inactivos, [168, 62, 82]);

    // Footer en última página
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setTextColor(160, 160, 170);
      doc.setFontSize(8);
      doc.text(
        `BY Raíces · Página ${i} de ${totalPages}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' }
      );
    }

    const fechaFile = new Date().toISOString().slice(0, 10);
    doc.save(`vendedores_byraices_${fechaFile}.pdf`);
  };

  if (error) {
    return <p className={styles.feedback}>Error: {error}</p>;
  }

  if (!Array.isArray(sellers)) {
    return <p className={styles.feedback}>Hubo un problema al cargar los vendedores.</p>;
  }

  return (
    <div className={styles.containerWrapper}>
      <div className={styles.toolbar}>
        <div className={styles.filterGroup}>
          <button
            className={`${styles.filterButton} ${filter === 'active' ? styles.active : ''}`}
            onClick={() => setFilter('active')}
          >
            Activos
          </button>
          <button
            className={`${styles.filterButton} ${filter === 'inactive' ? styles.active : ''}`}
            onClick={() => setFilter('inactive')}
          >
            Inactivos
          </button>
          <button
            className={`${styles.filterButton} ${filter === 'all' ? styles.active : ''}`}
            onClick={() => setFilter('all')}
          >
            Todos
          </button>
        </div>

        <button className={styles.pdfButton} onClick={generatePDF}>
          📄 Generar PDF
        </button>
      </div>

      <h2 className={styles.quantityTitle}>
        Mostrando <span>{numVendedores}</span> vendedor{numVendedores === 1 ? '' : 'es'}
      </h2>

      {filteredSellers.length === 0 ? (
        <p className={styles.feedback}>No hay vendedores que coincidan con el filtro.</p>
      ) : (
        <div className={styles.cardContainer}>
          {filteredSellers.map((seller) => {
            const propertiesCount = sellerProperties?.[seller.id]?.length || 0;
            return (
              <CardSellers
                key={seller.id}
                seller={seller}
                propertiesCount={propertiesCount}
              />
            );
          })}
        </div>
      )}

      <h3 className={styles.totalPropertiesTitle}>
        Total de propiedades: <span>{totalPropertiesCount}</span>
      </h3>
    </div>
  );
};

export default CardSellersContainer;
